import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import Tooltip from 'components/shared/Tooltip';
import { colors } from 'styles/theme/colors';

import { formatPercentage } from '../constants';

import { TopEntity } from './useSlideoutAnalytics';

type Tone = 'green' | 'red' | 'default';

type StatTileProps = {
  label: string;
  primary: ReactNode;
  addendum?: ReactNode;
  tone?: Tone;
  loading?: boolean;
};

// Vertical stack: label, big primary value, optional small grey addendum underneath.
// Used for every metric panel in the slideout headers (rate tiles, top-by tiles, summary tiles).
export function StatTile({ label, primary, addendum, tone = 'default', loading = false }: StatTileProps) {
  // A colored em-dash reads as broken; fall back to default tone whenever the value is empty.
  const effectiveTone: Tone = loading || primary == null || primary === '—' ? 'default' : tone;
  return (
    <Tooltip text={primary} disabled={loading} position='top'>
      <StatBox>
        <StatLabel>{label}</StatLabel>
        {loading ? (
          <SpinnerRow>
            <CircularProgress color={colors.primaryBlue} size={20} thickness={2} />
          </SpinnerRow>
        ) : (
          <>
            <PrimaryValue $tone={effectiveTone}>{primary}</PrimaryValue>
            {addendum != null && <Addendum>{addendum}</Addendum>}
          </>
        )}
      </StatBox>
    </Tooltip>
  );
}

// Three-tile row used by every rate metric drill (Acceptance, Denial): one rate tile plus a
// top-by receiving partner and a top-by plan, both formatted as "X.X% <ratePhrase>".
export function RateMetricTiles({
  rateLabel,
  rateTone,
  rate,
  count,
  total,
  ratePhrase,
  loading,
  topReceivingLoading,
  topPlanLoading,
  topReceivingLabel,
  topReceiving,
  topPlanLabel,
  topPlan,
}: {
  rateLabel: string;
  rateTone: Tone;
  rate: number | null;
  count: number;
  total: number;
  ratePhrase: string;
  loading: boolean;
  topReceivingLoading: boolean;
  topPlanLoading: boolean;
  topReceivingLabel: string;
  topReceiving: TopEntity;
  topPlanLabel: string;
  topPlan: TopEntity;
}) {
  return (
    <>
      <StatTile
        label={rateLabel}
        tone={rateTone}
        loading={loading}
        primary={formatPercentage(rate)}
        addendum={rate != null ? `${count} of ${total} referrals` : undefined}
      />
      <StatTile
        label={topReceivingLabel}
        loading={topReceivingLoading}
        primary={topReceiving?.name ?? '—'}
        addendum={topReceiving ? `${formatPercentage(topReceiving.value)} ${ratePhrase}` : undefined}
      />
      <StatTile
        label={topPlanLabel}
        loading={topPlanLoading}
        primary={topPlan?.name ?? '—'}
        addendum={topPlan ? `${formatPercentage(topPlan.value)} ${ratePhrase}` : undefined}
      />
    </>
  );
}

const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Allow children to shrink below their content width so ellipsis truncation actually kicks in
  // when the tile is in a grid column narrower than the value/addendum text.
  min-width: 0;
`;

export const TilesGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, 1fr);
  gap: 12px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 8px;

    ${StatBox} {
      background-color: #f2f3f4;
      border: none;
    }

    ${({ $columns }) =>
      $columns === 4 &&
      css`
        grid-template-columns: 1fr 1fr;
        gap: 8px 4px;
      `}
  }
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;
`;

const PrimaryValue = styled.span<{ $tone: Tone }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'green') return theme.colors.accentGreen;
    if ($tone === 'red') return theme.colors.accentRed;
    return theme.colors.black;
  }};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpinnerRow = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
`;

const Addendum = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
